import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  AppBar,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
  Fade,
  Slide,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LibraryBooksRoundedIcon from "@mui/icons-material/LibraryBooksRounded";
import { alpha, darken } from "@mui/material/styles";

import SimpleModalCompact from "../../common/ModalCompact/SimpleModalCompact";

import StepBoardName from "./steps_adv/StepBoardName";
import StepChooseColumns from "./steps_adv/StepChooseColumns";
import StepAddViewLayout from "./steps_adv/StepAddViewLayout";
import StepListProjects from "./steps_adv/StepListProjects";
import SummaryEditor from "./steps_adv/SummaryEditor";
import StepTemplateGallery from "./steps_adv/StepTemplateGallery";
import { EmptyWorkspaceHero } from "./Illustrations";

import { TeamWorkspaceHero, TemplateCreateHero, GuideWizardHero } from "./Illustrations";

const MERGE_IMPORT_IN_TEMPLATE = true;

const ENTRY_TONES = {
  blank: "#EDF3FF",
  guide: "#F3EDFF",
  template: "#FFF4E9",
  import: "#EFFFF6",
};

const ACCENTS = {
  entry: { bg: "#F7F8FF", dot: "#7B61FF" },
  templateGallery: { bg: "#FFF8F1", dot: "#FF8A3D" },
  verticalsFocus: { bg: "#F3FBFF", dot: "#30B1FF" },
  team: { bg: "#F4FFF5", dot: "#24C26A" },
  boardName: { bg: "#FFF3F7", dot: "#FF5C9A" },
  columns: { bg: "#F1F6FF", dot: "#3A7BFF" },
  views: { bg: "#FFF8F1", dot: "#FF8A3D" },
  items: { bg: "#F4FFF5", dot: "#24C26A" },
  review: { bg: "#F6F8FB", dot: "#7B8DAA" },
};

export const HERO_COLORS = {
  background: "#F5FDFF",
  surface: "#FFFFFF",
  uiGray: "#EDEEF0",
  slate: "#7D88A2",
  yellow: "#FBD87D",
  cream: "#FAF8DB",
  mint: "#95EDB9",
  sky: "#54A7CD",
  lavender: "#E1ACD6",
  blush: "#F7CBD4",
};

const HERO = {
  entry: { bg1: "#E6E0FF", bg2: "#C5B6FF", blob1: "#7B61FF", blob2: "#B9A7FF", ring: "#7B61FF", dark: false },
  templateGallery: { bg1: "#FFE6D3", bg2: "#FFC49B", blob1: "#FF8A3D", blob2: "#FFB47A", ring: "#FF8A3D", dark: false },
  verticalsFocus: { bg1: "#D9F3FF", bg2: "#BCEBFF", blob1: "#30B1FF", blob2: "#8FD6FF", ring: "#30B1FF", dark: false },
  team: { bg1: "#0A1330", bg2: "#081F46", blob1: "#24C26A", blob2: "#6CF0B6", ring: "#24C26A", dark: true },
  boardName: { bg1: "#FFE0EE", bg2: "#FFB3D0", blob1: "#FF5C9A", blob2: "#FF8EB8", ring: "#FF5C9A", dark: false },
  columns: { bg1: "#DBE6FF", bg2: "#C3D6FF", blob1: "#3A7BFF", blob2: "#9CB8FF", ring: "#3A7BFF", dark: false },
  views: { bg1: "#FFE6D3", bg2: "#FFC49B", blob1: "#FF8A3D", blob2: "#FFB47A", ring: "#FF8A3D", dark: false },
  items: { bg1: "#DDFCEB", bg2: "#B9F6D7", blob1: "#24C26A", blob2: "#8CE7B1", ring: "#24C26A", dark: false },
  review: { bg1: "#E7ECF4", bg2: "#D9E1EC", blob1: "#7B8DAA", blob2: "#B6C2D1", ring: "#7B8DAA", dark: false },
};

export default function WorkspaceWizardModal({ open, onClose, onCreated }) {

  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [mode, setMode] = useState(null);

  const [verticals, setVerticals] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);

  const [typeKey, setTypeKey] = useState(null);
  const [name, setName] = useState("");
  const [columns, setColumns] = useState(["owner", "status", "due"]);
  const [views, setViews] = useState(["table"]);
  const [initialItems, setInitialItems] = useState(["Uppgift 1"]);
  const [invites, setInvites] = useState([]);
  const [inviteLink] = useState("https://adress_to_copy_example.se");

  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const onHeroMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setParallax({ x: px, y: py });
  }, []);

  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const motion = prefersReducedMotion ? { x: 0, y: 0 } : parallax;

  const [description, setDescription] = useState("");
  const [storageGB, setStorageGB] = useState(1);
  const [icon, setIcon] = useState("mdi:book-open");
  const [color, setColor] = useState("#6a2135");
  const [imageUrl, setImageUrl] = useState(null);
  const [template, setTemplate] = useState("");
  const [owner, setOwner] = useState("");
  const [orderGroup, setOrderGroup] = useState("");
  const [network, setNetwork] = useState("Wilma network");
  const [menus, setMenus] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dashboard, setDashboard] = useState("standard");
  const [startPage, setStartPage] = useState("översikt");

  const patch = (fn) => {
    const draft = { description, storageGB, icon, color, imageUrl, template, owner, orderGroup, network, menus, roles, dashboard, startPage, name };
    fn(draft);
    setDescription(draft.description);
    setStorageGB(draft.storageGB);
    setIcon(draft.icon);
    setColor(draft.color);
    setImageUrl(draft.imageUrl);
    setTemplate(draft.template);
    setOwner(draft.owner);
    setOrderGroup(draft.orderGroup);
    setNetwork(draft.network);
    setMenus(draft.menus);
    setRoles(draft.roles);
    setDashboard(draft.dashboard);
    setStartPage(draft.startPage);
    setName(draft.name);
  };

  useEffect(() => {
    if (!open) return;
    setActive(0);
    setBusy(false);
    setError(null);
    setDone(null);
    setMode(null);
    setVerticals([]);
    setFocusAreas([]);
    setTypeKey(null);
    setName("");
    setColumns(["owner", "status", "due"]);
    setViews(["table"]);
    setInitialItems(["Uppgift 1"]);
    setInvites([]);
  }, [open]);

  const computedSteps = useMemo(() => {
    const arr = [];
    arr.push({ id: "entry", label: "Skapa arbetsyta" });
    if (mode === "template") arr.push({ id: "templateGallery", label: "Välj mall eller importera" });
    if (mode === "guide") arr.push({ id: "verticalsFocus", label: "Hantera & fokus" });
    arr.push({ id: "team", label: "Team" });
    const selected = [...verticals, ...focusAreas].map((s) => s.toLowerCase());
    const triggers = ["projekt", "project", "ärenden", "issue", "crm", "programutveckling", "utveckling", "pmo", "portfölj", "marknadsföring", "försäljning", "drift", "it-service", "service"];
    const hasProjectOrIssue = selected.some((x) => triggers.some((t) => x.includes(t)));
    if (mode === "guide" ? hasProjectOrIssue : true) {
      arr.push({ id: "boardName", label: "Namn" });
      arr.push({ id: "columns", label: "Kolumner" });
      arr.push({ id: "views", label: "Vyer" });
      arr.push({ id: "items", label: "Uppgifter" });
    }
    arr.push({ id: "review", label: "Sammanfattning" });
    return arr;
  }, [mode, verticals, focusAreas]);

  const canNext = useMemo(() => {
    const stepId = computedSteps[active]?.id;
    if (stepId === "entry") return Boolean(mode);
    if (stepId === "templateGallery") return Boolean(typeKey);
    if (stepId === "boardName") return Boolean(name.trim());
    if (stepId === "verticalsFocus") return verticals.length > 0 && focusAreas.length > 0;
    return true;
  }, [active, computedSteps, mode, typeKey, name, verticals, focusAreas]);

  const next = () => setActive((a) => Math.min(a + 1, computedSteps.length - 1));

  const resetEntryChoice = useCallback(() => {
    setMode(null);
    setVerticals([]);
    setFocusAreas([]);
    setTypeKey(null);
  }, []);

  const back = () => {
    setActive((a) => {
      const newActive = Math.max(a - 1, 0);
      if (newActive === 0) resetEntryChoice();
      return newActive;
    });
  };

  const prevStepRef = useRef(0);
  const [dir, setDir] = useState("left");
  useEffect(() => {
    const prev = prevStepRef.current;
    setDir(active >= prev ? "left" : "right");
    prevStepRef.current = active;
  }, [active]);

  async function handleCreate() {
    setBusy(true);
    setError(null);

    try {
      /*setDone(ws);
      if (onCreated) onCreated(ws);*/
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  const accent = ACCENTS[computedSteps[active]?.id || "entry"];
  const hero = HERO[computedSteps[active]?.id || "entry"];
  const showTimeline = false;
  const stepId = computedSteps[active]?.id;

  const HeroArt =
    stepId === "templateGallery" || mode === "template"
      ? TemplateCreateHero
      : stepId === "guide" || mode === "guide"
        ? GuideWizardHero
        : mode === "blank"
          ? EmptyWorkspaceHero
          : TeamWorkspaceHero;

  // <<< NYTT: plain-läge för tom arbetsyta (ingen glasram/blur osv.)
  const isPlainHero = mode === "blank";

  return (
    <SimpleModalCompact
      open={open} title={<Typography variant="h6" sx={{ fontWeight: 600 }}>Skapa arbetsyta</Typography>}
      onClose={onClose}
      sx={{ "& .MuiPaper-root": { backgroundColor: "#fff" }, "& .MuiDialogContent-root": { p: 0 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "calc(100vh - 32px)",
        }}
      >
        <AppBar elevation={0} color="transparent" position="sticky" sx={{ background: "linear-gradient(180deg, rgba(255,255,255,.85), rgba(255,255,255,.65))", backdropFilter: "saturate(1.1) blur(10px)" }}>
          {showTimeline && (<ProgressTimeline steps={computedSteps} active={active} accent={accent.dot} />)}
        </AppBar>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            p: { xs: 2, md: 3 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) clamp(420px, 32vw, 520px)" },
            gap: { xs: 2, md: 3 },
            alignItems: { xs: "start", md: "stretch" },
            minHeight: { md: 520 },
          }}
        >
          {/* --- vänsterkort --- */}
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 3,
              boxShadow: "0 12px 32px rgba(0,0,0,.06)",
              overflow: "hidden",
              minWidth: 0,
              height: "100%",
              display: "grid",
              gridTemplateRows: "auto 1fr auto",
            }}
          >
            {error && (<Alert severity="error" sx={{ m: 2 }}>{error}</Alert>)}
            {done && (<Alert severity="success" sx={{ m: 2 }}>Arbetsytan skapades!</Alert>)}

            <Box sx={{ minHeight: 0, overflowY: "auto" }}>
              <StepTransition dir={dir}>
                <StepBody
                  key={computedSteps[active]?.id}
                  steps={computedSteps}
                  step={active}
                  mode={mode}
                  setMode={setMode}
                  tone={accent.dot}
                  onAdvance={next}
                  value={{
                    typeKey, name, columns, views, initialItems, invites, inviteLink, verticals, focusAreas, description, storageGB, icon, color, imageUrl, template, owner, orderGroup, network, menus, roles, dashboard, startPage, }}
                  onTypePick={setTypeKey}
                  onName={(v) => setName(v?.name ?? "")}
                  onColumns={(v) => setColumns(v?.columns ?? [])}
                  onViews={(v) => setViews(v?.views ?? [])}
                  onItems={(v) => setInitialItems(v?.initialItems ?? [])}
                  onInvites={(v) => setInvites(v?.invites ?? [])}
                  onVerticals={setVerticals}
                  onFocusAreas={setFocusAreas}
                  onPatch={patch}
                />
              </StepTransition>
            </Box>

            <Divider />

            <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2 }}>
              <Button startIcon={<ArrowBackRoundedIcon />} onClick={back} disabled={active === 0}>Tillbaka</Button>
              <Box sx={{ flex: 1 }} />
              {active < computedSteps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={next}
                  disabled={!canNext}
                  sx={(t) => ({
                    px: 2.5,
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundImage: `linear-gradient(180deg, ${accent.dot}, ${darken(accent.dot, 0.08)})`,
                    boxShadow: `0 12px 28px ${alpha(accent.dot, 0.30)}`,
                    border: "1px solid",
                    borderColor: alpha("#000", 0.06),
                    transition: "all .25s cubic-bezier(.2,.8,.2,1)",
                    "&:hover": {
                      boxShadow: `0 14px 32px ${alpha(accent.dot, 0.38)}`,
                      transform: "translateY(-1px)",
                    },
                    "&.Mui-disabled": {
                      backgroundImage: "none",
                      bgcolor: t.palette.action.disabledBackground,
                      color: t.palette.text.disabled,
                      boxShadow: "none",
                      transform: "none",
                    },
                  })}
                >
                  Nästa
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleCreate}
                  disabled={busy}
                  sx={{
                    px: 2.5, borderRadius: 2, textTransform: "none",
                    backgroundImage: "linear-gradient(180deg, #7B61FF, #6C55F5)",
                    boxShadow: "0 12px 28px rgba(123,97,255,.30)",
                    "&:hover": { boxShadow: "0 14px 32px rgba(123,97,255,.38)", transform: "translateY(-1px)" },
                    transition: "all .25s cubic-bezier(.2,.8,.2,1)"
                  }}
                >
                  Skapa arbetsyta
                </Button>
              )}
            </Stack>
          </Box>

          {/* --- HERO --- */}
          <Box
            onMouseMove={onHeroMouseMove}
            sx={{
              display: { xs: "none", md: "flex" },
              position: "relative",
              alignItems: "stretch",
              justifyContent: "center",
              borderRadius: 3,
              overflow: "hidden",
              background: isPlainHero
                ? "linear-gradient(135deg, rgba(123,97,255,0.16), rgba(123,97,255,0.08))"              // tidigare: "transparent"
                : `linear-gradient(135deg, ${alpha(hero.ring, 0.08)}, ${alpha(hero.ring, 0.04)})`,
              boxShadow: isPlainHero
                ? "0 24px 60px rgba(0,0,0,.10)"                                        // "none" <-- ingen stor skugga runt panelen
                : hero.dark
                  ? "0 40px 90px rgba(0,0,0,.38)"
                  : "0 24px 60px rgba(0,0,0,.10)",
            }}
          >
            {/* Orbits endast i glas-lägen */}
            {!isPlainHero && (
              <Box sx={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}>
                <OrbitRings color={hero.ring} parallax={parallax} />
              </Box>
            )}

            {/* Inner panel – glas i vanliga lägen, ren i blank-läget */}
            <Box
              sx={{
                m: { xs: 2, md: 3 },
                p: isPlainHero ? 0 : { xs: 1.5, md: 2.25 },
                borderRadius: isPlainHero ? 0 : 4,
                alignSelf: "center",
                width: "100%",
                maxWidth: 780,
                zIndex: 2,
                background: isPlainHero
                  ? "transparent"                                  // <-- ingen glasgradient
                  : "linear-gradient(180deg, rgba(255,255,255,.78), rgba(255,255,255,.62))",
                boxShadow: isPlainHero
                  ? "none"
                  : "0 18px 42px rgba(17,24,39,.16), inset 0 0 0 1px rgba(255,255,255,.72)",
                transform: isPlainHero
                  ? "none"
                  : `translate3d(${motion.x * 4}px, ${motion.y * -4}px, 0)`,
                transition: "transform .25s cubic-bezier(.2,.8,.2,1)",
                "&::after": isPlainHero
                  ? { content: '""', display: "none" }             // <-- ingen vignett
                  : {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: 24,
                    pointerEvents: "none",
                    background: "radial-gradient(120% 80% at 70% 30%, rgba(0,0,0,.07) 0%, transparent 62%)",
                  },
              }}
            >
              <HeroArt
                mode="portrait"
                tint={isPlainHero ? undefined : hero.ring}
                strength={0.14}
                fit="height"
              />
            </Box>

            {/* Glow-dots bara i glas-lägen */}
            {!isPlainHero && (
              <Box sx={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none" }}>
                <Box sx={{
                  position: "absolute", left: "78%", top: "22%",
                  width: 14, height: 14, borderRadius: "50%",
                  backgroundColor: hero.ring,
                  boxShadow: `0 6px 18px ${alpha(hero.ring, 0.55)}, 0 0 0 6px ${alpha(hero.ring, 0.18)}`,
                  transform: `translate3d(${parallax.x * 8}px, ${parallax.y * 8}px, 0)`,
                  transition: "transform .25s cubic-bezier(.2,.8,.2,1)"
                }} />
                <Box sx={{
                  position: "absolute", left: "12%", bottom: "10%",
                  width: 12, height: 12, borderRadius: "50%",
                  backgroundColor: alpha(hero.ring, .9),
                  boxShadow: `0 4px 14px ${alpha(hero.ring, 0.45)}, 0 0 0 5px ${alpha(hero.ring, 0.16)}`,
                  transform: `translate3d(${parallax.x * -6}px, ${parallax.y * -6}px, 0)`,
                  transition: "transform .25s cubic-bezier(.2,.8,.2,1)"
                }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </SimpleModalCompact>
  );
}

function StepTransition({ children, dir = "left" }) {
  const inFrom = dir === "left" ? "left" : "right";
  return (
    <Fade in timeout={200}>
      <Slide in direction={inFrom} timeout={320} mountOnEnter unmountOnExit>
        <Box>{children}</Box>
      </Slide>
    </Fade>
  );
}

function OrbitRings({ color, parallax }) {
  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", display: "grid", placeItems: "center", transform: `translate3d(${parallax.x * 2}px, ${parallax.y * 2}px, 0)`, transition: "transform .25s cubic-bezier(.2,.8,.2,1)" }}>
      {[180, 260, 340].map((size, i) => (
        <Box key={size} sx={{ width: size, height: size, borderRadius: "50%", border: `1.5px solid ${alpha(color, 0.36 - i * 0.12)}` }} />
      ))}
    </Box>
  );
}

function HeroGlyph({ color, parallax, dark }) {
  return (
    <Box
      sx={{
        position: "absolute",
        right: 24,
        bottom: 20,
        transform: `translate3d(${parallax.x * -3}px, ${parallax.y * 2}px, 0)`,
        transition: "transform .25s cubic-bezier(.2,.8,.2,1)",
        opacity: dark ? 0.9 : 0.85,
      }}
    >
      <svg width="140" height="96" viewBox="0 0 140 96" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="8" width="54" height="36" rx="8" stroke={color} strokeOpacity="0.6" strokeWidth="2" />
        <rect x="64" y="2" width="74" height="28" rx="8" stroke={color} strokeOpacity="0.35" strokeWidth="2" />
        <rect x="64" y="36" width="34" height="22" rx="6" stroke={color} strokeOpacity="0.5" strokeWidth="2" />
        <rect x="104" y="36" width="34" height="22" rx="6" stroke={color} strokeOpacity="0.18" strokeWidth="2" />
        <circle cx="20" cy="64" r="10" fill={color} fillOpacity="0.25" />
        <circle cx="48" cy="64" r="10" fill={color} fillOpacity="0.15" />
      </svg>
    </Box>
  );
}

function ProgressTimeline({ steps, active, accent }) {
  const total = steps.length;
  const pct = Math.round(((active + 1) / total) * 100);
  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 1.25 }}>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: "divider", position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${pct}%`,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.6)})`,
            transition: "width .35s cubic-bezier(.2,.8,.2,1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            gridTemplateColumns: `repeat(${total}, 1fr)`,
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          {steps.map((s, i) => {
            const reached = i <= active;
            return (
              <Box key={s.id} sx={{ position: "relative" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: reached ? accent : "#CBD5E1",
                    outline: `2px solid ${reached ? "#fff" : "transparent"}`,
                    boxShadow: reached ? `0 0 0 6px ${alpha(accent, 0.18)}` : "none",
                    transition:
                      "background-color .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s cubic-bezier(.2,.8,.2,1), outline-color .25s cubic-bezier(.2,.8,.2,1)",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: { xs: "none", md: "block" },
                    position: "absolute",
                    top: 16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    whiteSpace: "nowrap",
                    color: reached ? "text.primary" : "text.disabled",
                    fontWeight: reached ? 600 : 500,
                  }}
                >
                  {s.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

/* ---------- Stegkroppar ---------- */
function StepBody({
  steps,
  step,
  tone,
  mode,
  setMode,
  onAdvance,
  value,
  onTypePick,
  onName,
  onColumns,
  onViews,
  onItems,
  onInvites,
  onVerticals,
  onFocusAreas,
  onPatch,
}) {
  const id = steps[step]?.id;
  switch (id) {
    case "entry":
      return <StepEntryMode mode={mode} setMode={setMode} tone={tone} onChosen={onAdvance} />;
    case "verticalsFocus":
      return (
        <Box>
          <StepVerticals value={value.verticals} onChange={onVerticals} />
          <Divider sx={{ my: 3 }} />
          <StepFocusAreas value={value.focusAreas} onChange={onFocusAreas} />
        </Box>
      );
    case "team":
      return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <StepTeamInvite value={value.invites} inviteLink={value.inviteLink} onNext={onInvites} />
        </Box>
      );
    case "boardName":
      return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <StepBoardName value={{ name: value.name }} onNext={onName} showActions={false} />
        </Box>
      );
    case "columns":
      return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <StepChooseColumns value={value.columns} onNext={onColumns} showActions={false} />
        </Box>
      );
    case "views":
      return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <StepAddViewLayout value={value.views} onNext={onViews} showActions={false} />
        </Box>
      );
    case "items":
      return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <StepListProjects value={value.initialItems} onNext={onItems} showActions={false} />
        </Box>
      );
    case "review":
      return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Sammanfattning
          </Typography>
          {/*<SummaryList data={value} />*/}
          <SummaryEditor value={value} onPatch={onPatch} />
        </Box>
      );
    case "templateGallery":
      return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <StepTemplateGallery value={{ template: value.typeKey }} onNext={onTypePick} />
        </Box>
      );
    default:
      return null;
  }
}

/* ---------- Entry ---------- */
function StepEntryMode({ mode, setMode, tone, onChosen }) {
  const options = MERGE_IMPORT_IN_TEMPLATE
    ? [
      { key: "blank", title: "Tom arbetsyta", subtitle: "Bygg arbetsytan från grunden och forma allt själv.", Icon: DashboardCustomizeRoundedIcon, tone: ENTRY_TONES.blank },
      { key: "guide", title: "Från guide", subtitle: "Skapa arbetsyta genom att besvara frågor, så ställs den in automatiskt.", Icon: AutoAwesomeRoundedIcon, tone: ENTRY_TONES.guide },
      { key: "template", title: "Från mall / importera", subtitle: "Skapa arbetsyta från mall eller importera från fil/befintlig.", Icon: LibraryBooksRoundedIcon, tone: ENTRY_TONES.template },
    ]
    : [
      { key: "blank", title: "Tom arbetsyta", subtitle: "Bygg från grunden och forma allt själv.", Icon: DashboardCustomizeRoundedIcon, tone: ENTRY_TONES.blank },
      { key: "template", title: "Från en mall", subtitle: "Välj från bibliotek.", Icon: LibraryBooksRoundedIcon, tone: ENTRY_TONES.template },
      { key: "guide", title: "Guide", subtitle: "Svara på några enkla frågor och få rätt konfiguration automatiskt.", Icon: AutoAwesomeRoundedIcon, tone: ENTRY_TONES.guide },
      { key: "import", title: "Importera", subtitle: "Konfigurera från filer eller en befintlig arbetsyta.", Icon: FileUploadRoundedIcon, tone: ENTRY_TONES.import },
    ];

  const handlePick = (key) => {
    setMode(key);
    if (typeof onChosen === "function") onChosen();
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2.5 }, display: "grid", gap: 2 }}>
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.15 }} gutterBottom>
          1. Skapa ny arbetsyta
        </Typography>
        <Typography sx={{ mb: 2 }}>
          Kickstarta ert arbete på det sätt som passar er bäst:
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fit, minmax(220px, 1fr))" }, gap: 1.2 }}>
          {options.map(({ key, title, subtitle, Icon, tone }) => (
            <ChoiceButton key={key} active={mode === key} onClick={() => handlePick(key)} title={title} subtitle={subtitle} Icon={Icon} tone={tone} />
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            p: 1.25,
            mt: 2.5,
            borderRadius: 2,
            bgcolor: alpha(tone, 0.10),
            border: "1px solid",
            borderColor: alpha(tone, 0.22),
            boxShadow: `inset 4px 0 0 ${alpha(tone, 0.45)}`,
            color: "text.secondary",
            backdropFilter: "saturate(1.02) blur(3px)",
            "& .MuiSvgIcon-root": { color: alpha(tone, 0.9) },
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            En arbetsyta är en gemensam plats där ditt team samarbetar, samlar material,
            organiserar arbete och hanterar processer – med de funktioner som aktiverats.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ChoiceButton({ active, onClick, title, subtitle, Icon, tone = "#F5F5F5" }) {
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={(t) => ({
        paddingTop: 2.25,
        paddingBottom: 2.25,
        paddingLeft: 2,
        paddingRight: 2,
        width: "100%",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
        borderRadius: 2.5,
        background:
          `linear-gradient(${t.palette.background.paper}, ${t.palette.background.paper}) padding-box,` +
          `linear-gradient(135deg, ${alpha(tone, 0.96)}, ${alpha(t.palette.primary.main, 0.35)}) border-box`,
        border: "1px solid transparent",
        boxShadow: active ? "0 8px 24px rgba(25,118,210,.18)" : "0 6px 18px rgba(0,0,0,.06)",
        transition: "transform .16s ease, box-shadow .22s ease, filter .22s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 18px 36px rgba(0,0,0,.12)",
          filter: "saturate(1.06)",
        },
        "&:active": {
          transform: "translateY(0)",
          boxShadow: "0 6px 18px rgba(0,0,0,.10)",
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px ${alpha(t.palette.primary.main, 0.18)}`,
        },
      })}
    >
      {Icon ? (
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: alpha(tone, 0.98),
            border: "1px solid",
            borderColor: alpha("#000", 0.06),
            boxShadow: `inset 0 -6px 14px ${alpha("#000", 0.04)}`,
          }}
        >
          <Icon fontSize="medium" />
        </Box>
      ) : null}
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontSize: { xs: 16.5, md: 17.5 }, lineHeight: 1.25, opacity: 0.9 }}
        >
          {title}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>
          {subtitle}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

function StepVerticals({ value = [], onChange }) {
  const options = [
    "Design och reklam",
    "IT",
    "Drift",
    "Försäljning och CRM",
    "HR och rekrytering",
    "Produkthantering",
    "Bygg och anläggning",
    "Juridisk",
    "PMO",
    "Finans",
    "Programutveckling",
    "Ideellt arbete",
    "Marknadsföring",
    "Utbildning",
    "Annat",
  ];
  return (
    <ChipPickList
      title="2. Välj vad du vill hantera först"
      subtitle="Du kan alltid lägga till fler i framtiden"
      options={options}
      value={value}
      onChange={onChange}
    />
  );
}

function StepFocusAreas({ value = [], onChange }) {
  const options = [
    "Mål och strategi",
    "Uppgiftshantering",
    "CRM",
    "IT-service",
    "Programutveckling",
    "Ärenden och förfrågningar",
    "Resurshantering",
    "Projektledning",
    "Kunskapsbas",
    "Portföljhantering",
    "Annat",
  ];
  return (
    <ChipPickList
      title="3. Välj vad du vill fokusera på först"
      subtitle="Hjälp oss att skräddarsy din bästa upplevelse"
      options={options}
      value={value}
      onChange={onChange}
    />
  );
}

function ChipPickList({ title, subtitle, options, value = [], onChange }) {
  const toggle = (opt) => {
    const checked = value.includes(opt);
    onChange(checked ? value.filter((x) => x !== opt) : [...value, opt]);
  };
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        {title}
      </Typography>
      {subtitle ? <Typography sx={{ mb: 2 }}>{subtitle}</Typography> : null}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {options.map((opt) => {
          const checked = value.includes(opt);
          return (
            <Chip
              key={opt}
              label={opt}
              onClick={() => toggle(opt)}
              color={checked ? "primary" : "default"}
              variant={checked ? "filled" : "outlined"}
              icon={checked ? <CheckRoundedIcon /> : undefined}
              sx={{
                borderRadius: 999,
                height: 36,
                "& .MuiChip-icon": { ml: 0.5 },
                transition: "transform .12s ease, box-shadow .15s ease, background-color .2s ease, opacity .15s ease",
                willChange: "transform",
                "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 14px rgba(0,0,0,.06)" },
                "&:active": { transform: "translateY(0) scale(0.995)" },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function StepTeamInvite({ value = [], inviteLink, onNext }) {
  const [rows, setRows] = useState(
    value.length
      ? value
      : [
        { email: "", role: "Admin" },
        { email: "", role: "Medlem" },
        { email: "", role: "Medlem" },
      ]
  );

  const addRow = () => setRows((r) => [...r, { email: "", role: "Medlem" }]);
  const update = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  useEffect(() => {
    onNext(rows);
  }, [rows, onNext]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
    } catch {
      const el = document.createElement("textarea");
      el.value = inviteLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        4. Vem mer finns i ditt team?
      </Typography>

      <Box sx={{ mt: 1, mb: 2, p: 2, borderRadius: 2, border: "1px dashed", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Bjud in folk med länk (alla med en e-postadress från @mindomain.se)
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ px: 1.25, py: 0.75, borderRadius: 1, bgcolor: "action.hover", fontFamily: "monospace" }}>
            {inviteLink}
          </Box>
          <Button size="small" onClick={copyLink}>
            Kopiera
          </Button>
        </Box>
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Bjud in med e-post
      </Typography>
      <Box sx={{ display: "grid", gap: 1 }}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 200px" }, gap: 1 }}>
            <TextField
              type="email"
              label="E-post"
              placeholder="Lägg till e-post här"
              value={row.email}
              onChange={(e) => update(i, { email: e.target.value })}
              size="small"
            />
            <FormControl size="small">
              <InputLabel id={`role-label-${i}`}>Roll</InputLabel>
              <Select labelId={`role-label-${i}`} label="Roll" value={row.role} onChange={(e) => update(i, { role: e.target.value })}>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Medlem">Medlem</MenuItem>
                <MenuItem value="Gäst">Gäst</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ))}
        <Button onClick={addRow} sx={{ justifySelf: "start" }}>
          + Lägg till en rad
        </Button>
      </Box>
    </Box>
  );
}

function SummaryList({ data }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2, "& li": { mb: 0.75 } }}>
      <li>
        <b>Läge:</b> {data.mode || "—"}
      </li>
      <li>
        <b>Vertikaler:</b> {data.verticals?.length ? data.verticals.join(", ") : "—"}
      </li>
      <li>
        <b>Fokus:</b> {data.focusAreas?.length ? data.focusAreas.join(", ") : "—"}
      </li>
      <li>
        <b>Typ:</b> {data.typeKey || "—"}
      </li>
      <li>
        <b>Namn:</b> {data.name?.trim() || "—"}
      </li>
      <li>
        <b>Kolumner:</b> {data.columns?.length ? data.columns.join(", ") : "—"}
      </li>
      <li>
        <b>Vyer:</b> {data.views?.length ? data.views.join(", ") : "—"}
      </li>
      <li>
        <b>Startinnehåll:</b> {data.initialItems?.length || 0} rader
      </li>
      <li>
        <b>Inbjudningar:</b> {data.invites?.filter(Boolean)?.length || 0} st
      </li>
    </Box>
  );
}

WorkspaceWizardModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
};
